// Locale-aware HTML email templates used when sending a customer offer by email.
// Placeholders in the form {{token}} are replaced by buildOfferEmailBody().
import { formatOfferDate } from './customer-offer-date.util';

export interface OfferEmailData {
  customerName: string;
  senderName: string;
  offerNumber: string;
  offeredServices: string;
  startDate: string;
  endDate: string | null;
  noEndDate: boolean;
  amount: number;
  currency: string;
  compensationType: string;
  locale: string;
}

const isSwedish = (locale: string): boolean => locale?.toLowerCase() === 'sv-se';

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string
  ));

const formatEmailDate = (iso: string): string => formatOfferDate(iso);

const formatEmailAmount = (amount: number, currency: string, locale: string): string =>
  `${amount.toLocaleString(isSwedish(locale) ? 'sv-SE' : 'en-US')} ${currency}`;

const COMPENSATION_TYPE_LABELS: Record<string, { en: string; sv: string }> = {
  per_hour: { en: 'Payment per hour', sv: 'Betalning per timme' },
  per_day: { en: 'Payment per day', sv: 'Betalning per dag' },
  per_week: { en: 'Payment per week', sv: 'Betalning per vecka' },
  per_month: { en: 'Payment per month', sv: 'Betalning per månad' },
  as_agreed: { en: 'Fixed price', sv: 'Fast pris' },
  per_unit: { en: 'Payment per unit', sv: 'Betalning per enhet' },
};

const formatCompensationType = (compensationType: string, locale: string): string => {
  const entry = COMPENSATION_TYPE_LABELS[compensationType];
  if (!entry) return '—';
  return isSwedish(locale) ? entry.sv : entry.en;
};

const OFFER_EMAIL_TEMPLATE_EN = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<link href="https://fonts.googleapis.com/css?family=IBM+Plex+Sans:ital,wght@0,400;0,500;0,600" rel="stylesheet" />
<link href="https://fonts.googleapis.com/css?family=Plus+Jakarta+Sans:ital,wght@0,400;0,600;0,700" rel="stylesheet" />
<title>Your offer from Invozio</title>
<style>
  body, table, td { font-family: 'Plus Jakarta Sans', Arial, Helvetica, sans-serif; }
  body { margin: 0; padding: 0; background-color: #f8f7ff; }
  table { border-collapse: collapse; }
  img { border: 0; line-height: 100%; }
</style>
</head>
<body style="margin:0;padding:0;background-color:#f8f7ff;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f8f7ff;">
    <tr>
      <td align="center" style="padding:24px 16px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;">
          <tr>
            <td style="background-color:#6558de;background-image:url('https://invozioprod.blob.core.windows.net/website-assets/img/news-letter/image-172331259259710-b6d24043.png');background-size:cover;background-position:center right;border-radius:10px 10px 0 0;padding:40px 48px;">
              <img src="https://invozioprod.blob.core.windows.net/website-assets/img/news-letter/logo_invozio_white-43ac7abd.png" width="140" height="42" alt="Invozio" style="display:block;margin-bottom:24px;" />
              <div style="font-family:'IBM Plex Sans', Arial, Helvetica, sans-serif;font-size:28px;line-height:130%;font-weight:500;color:#ffffff;">Your offer is ready</div>
            </td>
          </tr>
          <tr>
            <td style="background-color:#ffffff;padding:40px 48px 8px;">
              <p style="margin:0 0 16px;font-size:17px;line-height:150%;font-weight:600;color:#28243d;">Hello {{customerName}},</p>
              <p style="margin:0 0 24px;font-size:14px;line-height:150%;color:#28243d;">{{senderName}} has sent you a new customer offer through Invozio. You'll find a summary of the offer below, and the full offer is attached to this email as a PDF.</p>
            </td>
          </tr>
          <tr>
            <td style="background-color:#ffffff;padding:0 48px 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#eeedfe;border-radius:10px;">
                <tr>
                  <td style="padding:24px 28px;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Offer No.</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;font-weight:600;">{{offerNumber}}</td>
                      </tr>
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Offered Service</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;">{{offeredServices}}</td>
                      </tr>
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Offer Period</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;">{{startDate}} &ndash; {{endDateLabel}}</td>
                      </tr>
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Compensation</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;font-weight:600;">{{amountLabel}}</td>
                      </tr>
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Payment Mode</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;">{{paymentModeLabel}}</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="background-color:#ffffff;padding:0 48px 40px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#eeedfe;border-left:4px solid #6558de;border-radius:6px;margin:0 0 24px;">
                <tr>
                  <td style="padding:14px 18px;">
                    <p style="margin:0;font-size:13px;line-height:150%;color:#28243d;"><strong style="color:#6750b8;">Note:</strong> Please review the attached offer and accept or decline it using the link that will be provided separately.</p>
                  </td>
                </tr>
              </table>
              <p style="margin:0;font-size:14px;line-height:150%;color:#28243d;">Kind regards,<br /><strong>{{senderName}}</strong><br /><span style="font-style:italic;color:#28243dcc;">via Invozio</span></p>
            </td>
          </tr>
          <tr>
            <td style="background-color:#2e2552;border-radius:0 0 10px 10px;padding:32px 48px;text-align:center;">
              <img src="https://invozioprod.blob.core.windows.net/website-assets/img/news-letter/logo_white_small-71b12b71.png" width="120" height="36" alt="Invozio" style="display:block;margin:0 auto 16px;" />
              <p style="margin:0;font-size:11px;line-height:150%;color:#d1c3ff;font-family:'IBM Plex Sans', Arial, Helvetica, sans-serif;">&copy; {{currentYear}} Invozio. All rights reserved.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

const OFFER_EMAIL_TEMPLATE_SV = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<link href="https://fonts.googleapis.com/css?family=IBM+Plex+Sans:ital,wght@0,400;0,500;0,600" rel="stylesheet" />
<link href="https://fonts.googleapis.com/css?family=Plus+Jakarta+Sans:ital,wght@0,400;0,600;0,700" rel="stylesheet" />
<title>Ditt erbjudande från Invozio</title>
<style>
  body, table, td { font-family: 'Plus Jakarta Sans', Arial, Helvetica, sans-serif; }
  body { margin: 0; padding: 0; background-color: #f8f7ff; }
  table { border-collapse: collapse; }
  img { border: 0; line-height: 100%; }
</style>
</head>
<body style="margin:0;padding:0;background-color:#f8f7ff;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f8f7ff;">
    <tr>
      <td align="center" style="padding:24px 16px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;">
          <tr>
            <td style="background-color:#6558de;background-image:url('https://invozioprod.blob.core.windows.net/website-assets/img/news-letter/image-172331259259710-b6d24043.png');background-size:cover;background-position:center right;border-radius:10px 10px 0 0;padding:40px 48px;">
              <img src="https://invozioprod.blob.core.windows.net/website-assets/img/news-letter/logo_invozio_white-43ac7abd.png" width="140" height="42" alt="Invozio" style="display:block;margin-bottom:24px;" />
              <div style="font-family:'IBM Plex Sans', Arial, Helvetica, sans-serif;font-size:28px;line-height:130%;font-weight:500;color:#ffffff;">Ditt erbjudande är klart</div>
            </td>
          </tr>
          <tr>
            <td style="background-color:#ffffff;padding:40px 48px 8px;">
              <p style="margin:0 0 16px;font-size:17px;line-height:150%;font-weight:600;color:#28243d;">Hej {{customerName}},</p>
              <p style="margin:0 0 24px;font-size:14px;line-height:150%;color:#28243d;">{{senderName}} har skickat ett nytt kunderbjudande till dig via Invozio. Nedan ser du en sammanfattning av erbjudandet, och hela erbjudandet finns bifogat som PDF.</p>
            </td>
          </tr>
          <tr>
            <td style="background-color:#ffffff;padding:0 48px 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#eeedfe;border-radius:10px;">
                <tr>
                  <td style="padding:24px 28px;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Erbjudandenr.</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;font-weight:600;">{{offerNumber}}</td>
                      </tr>
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Erbjuden tjänst</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;">{{offeredServices}}</td>
                      </tr>
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Erbjudandeperiod</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;">{{startDate}} &ndash; {{endDateLabel}}</td>
                      </tr>
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Ersättning</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;font-weight:600;">{{amountLabel}}</td>
                      </tr>
                      <tr>
                        <td style="padding:6px 0;font-size:13px;color:#6750b8;font-weight:600;">Betalningssätt</td>
                        <td style="padding:6px 0;font-size:14px;color:#28243d;text-align:right;">{{paymentModeLabel}}</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="background-color:#ffffff;padding:0 48px 40px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#eeedfe;border-left:4px solid #6558de;border-radius:6px;margin:0 0 24px;">
                <tr>
                  <td style="padding:14px 18px;">
                    <p style="margin:0;font-size:13px;line-height:150%;color:#28243d;"><strong style="color:#6750b8;">Obs:</strong> Vänligen granska det bifogade erbjudandet och acceptera eller avböj det via länken som skickas separat.</p>
                  </td>
                </tr>
              </table>
              <p style="margin:0;font-size:14px;line-height:150%;color:#28243d;">Med vänliga hälsningar,<br /><strong>{{senderName}}</strong><br /><span style="font-style:italic;color:#28243dcc;">via Invozio</span></p>
            </td>
          </tr>
          <tr>
            <td style="background-color:#2e2552;border-radius:0 0 10px 10px;padding:32px 48px;text-align:center;">
              <img src="https://invozioprod.blob.core.windows.net/website-assets/img/news-letter/logo_white_small-71b12b71.png" width="120" height="36" alt="Invozio" style="display:block;margin:0 auto 16px;" />
              <p style="margin:0;font-size:11px;line-height:150%;color:#d1c3ff;font-family:'IBM Plex Sans', Arial, Helvetica, sans-serif;">&copy; {{currentYear}} Invozio. Med ensamrätt.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

export const buildOfferEmailSubject = (data: OfferEmailData): string =>
  isSwedish(data.locale)
    ? `Ditt erbjudande ${data.offerNumber} från ${data.senderName}`
    : `Your offer ${data.offerNumber} from ${data.senderName}`;

export const buildOfferEmailBody = (data: OfferEmailData): string => {
  const template = isSwedish(data.locale) ? OFFER_EMAIL_TEMPLATE_SV : OFFER_EMAIL_TEMPLATE_EN;

  const endDateLabel = data.noEndDate
    ? (isSwedish(data.locale) ? 'Inget slutdatum' : 'No end date')
    : formatEmailDate(data.endDate || '');

  const replacements: Record<string, string> = {
    customerName: escapeHtml(data.customerName),
    senderName: escapeHtml(data.senderName),
    offerNumber: escapeHtml(data.offerNumber),
    offeredServices: escapeHtml(data.offeredServices),
    startDate: formatEmailDate(data.startDate),
    endDateLabel,
    amountLabel: escapeHtml(formatEmailAmount(data.amount, data.currency, data.locale)),
    paymentModeLabel: escapeHtml(formatCompensationType(data.compensationType, data.locale)),
    currentYear: String(new Date().getFullYear()),
  };

  return Object.entries(replacements).reduce(
    (html, [token, value]) => html.split(`{{${token}}}`).join(value),
    template
  );
};
